import { useMutation, useQuery } from "@tanstack/react-query";
import { authClient } from "@/utils/auth/client";

// Enable Two-Factor Authentication
export const useEnableTotpMutation = (
    setIsLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    return useMutation({
        mutationFn: async (password: string) => {
            const result = await authClient.twoFactor.enable({ password });

            if (result.error) {
                throw new Error(result.error.message || "Failed to enable 2FA");
            }

            return result.data;
        },
        onMutate: () => {
            if (setIsLoading) setIsLoading(true);
        },
        onError: (error: any) => {
            console.error("Enable TOTP error:", error);
            const errorMessage = error.message || "Failed to enable 2FA";
            if (setError) setError(errorMessage);
        },
        onSettled: () => {
            if (setIsLoading) setIsLoading(false);
        },
    });
};

// Disable Two-Factor Authentication
export const useDisableTotpMutation = (
    setIsLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    return useMutation({
        mutationFn: async (password: string) => {
            const result = await authClient.twoFactor.disable({ password });

            if (result.error) {
                throw new Error(result.error.message || "Failed to disable 2FA");
            }

            return result.data;
        },
        onMutate: () => {
            if (setIsLoading) setIsLoading(true);
        },
        onError: (error: any) => {
            console.error("Disable TOTP error:", error);
            const errorMessage = error.message || "Failed to disable 2FA";
            if (setError) setError(errorMessage);
        },
        onSettled: () => {
            if (setIsLoading) setIsLoading(false);
        },
    });
};

// Verify TOTP Code
export const useVerifyTotpMutation = (
    setIsLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    return useMutation({
        mutationFn: async ({ code, trustDevice = true }: { code: string; trustDevice?: boolean }) => {
            const result = await authClient.twoFactor.verifyTotp({
                code,
                trustDevice,
            });

            if (result.error) {
                throw new Error(result.error.message || "Invalid verification code");
            }

            return result.data;
        },
        onMutate: () => {
            if (setIsLoading) setIsLoading(true);
        },
        onError: (error: any) => {
            console.error("Verify TOTP error:", error);
            const errorMessage = error.message || "Invalid verification code";
            if (setError) setError(errorMessage);
        },
        onSettled: () => {
            if (setIsLoading) setIsLoading(false);
        },
    });
};

// Verify Backup Code
export const useVerifyBackupCodeMutation = (
    setIsLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    return useMutation({
        mutationFn: async ({ code, trustDevice = true }: { code: string; trustDevice?: boolean }) => {
            const result = await authClient.twoFactor.verifyBackupCode({
                code,
                trustDevice,
            });

            if (result.error) {
                throw new Error(result.error.message || "Invalid backup code");
            }

            return result.data;
        },
        onMutate: () => {
            if (setIsLoading) setIsLoading(true);
        },
        onError: (error: any) => {
            console.error("Verify backup code error:", error);
            const errorMessage = error.message || "Invalid backup code";
            if (setError) setError(errorMessage);
        },
        onSettled: () => {
            if (setIsLoading) setIsLoading(false);
        },
    });
};

// Generate New Backup Codes
export const useGenerateBackupCodesMutation = (
    setIsLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    return useMutation({
        mutationFn: async (password: string) => {
            const result = await authClient.twoFactor.generateBackupCodes({ password });

            if (result.error) {
                throw new Error(result.error.message || "Failed to generate backup codes");
            }

            return result.data;
        },
        onMutate: () => {
            if (setIsLoading) setIsLoading(true);
        },
        onError: (error: any) => {
            console.error("Generate backup codes error:", error);
            const errorMessage = error.message || "Failed to generate backup codes";
            if (setError) setError(errorMessage);
        },
        onSettled: () => {
            if (setIsLoading) setIsLoading(false);
        },
    });
};

// Get TOTP URI for QR Code
export const useTotpUriQuery = (password: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: ["totp-uri", password],
        queryFn: async () => {
            const result = await authClient.twoFactor.getTotpUri({ password });

            if (result.error) {
                throw new Error(result.error.message || "Failed to get TOTP URI");
            }

            return result.data;
        },
        enabled: enabled && !!password,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};
